import { unwrapResult } from '@reduxjs/toolkit';
import { Form, Formik, FormikValues } from 'formik';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { VisibilityEnum } from 'modules/core/domain/interfaces/supabase/overlay/auxiliary-types';
import { Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import supabase from 'modules/core/supabase/supabase';
import { locationsToOptions } from 'modules/locations/domain/data-mappers/locations-to-options';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { removeChampionshipFromList } from 'modules/organizer/store/organizer';
import { fetchOrganizer } from 'modules/organizer/store/organizer/actions';
import { time } from 'shared/constants/time';
import { ChampionshipStateData } from 'modules/championship/store/championship';
import { deleteChampionship as deleteChampionshipAction, upsertChampionship } from 'modules/championship/store/championship/actions';
import {
  championshipDataSelector,
  championshipISavingSelector,
  championshipIsDeletingSelector,
} from 'modules/championship/store/championship/selectors';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePreventNavigation } from 'shared/hooks';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { Options } from 'shared/types/option';
import { TeamPlayersNumber } from 'shared/types/team-players-number';
import { getTeamPlayersNumberOptions } from 'shared/utils';
import { AppDispatch } from 'store/store';
import UDFormDateRange from 'ud-form/components/datetime/range';
import UDFormInput from 'ud-form/components/input';
import UDFormTextarea from 'ud-form/components/input/textarea';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import LocationSelect from 'modules/locations/ui/components/location-select';
import UdFormTemplateSelect from 'ud-form/components/select/ud-form-template-select';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import { useAsyncButton } from 'ud-ui/hooks/use-async-button';

const getTranslatedTeamPlayersNumberOption = (t: TFunction, playersNumber: number) => ({
  label: t(`championship.info.teamPlayersNumber.${playersNumber}`),
  value: playersNumber,
});

const getTranslatedTeamPlayersNumberOptions = (t: TFunction) =>
  getTeamPlayersNumberOptions().map((num) => getTranslatedTeamPlayersNumberOption(t, num));

type ChampionshipInfoFormProps = {
  tournamentId: number;
  championshipId?: number;
  initialValues?: {
    id?: number;
    name: string;
    startDate: Date;
    endDate: Date;
  };
  onCreateGameModalOpen?: () => void;
};

export const ChampionshipInfoForm = ({ tournamentId, championshipId }: ChampionshipInfoFormProps) => {
  const location = useLocation();
  const state = location.state as undefined | { highlight: string[] };
  const isCreate = useMemo(() => {
    return location.pathname.includes('create');
  }, [location.pathname]);

  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const championship: ChampionshipStateData | undefined = useSelector(championshipDataSelector(championshipId ?? -1));
  const navigate = useNavigate();

  if (!championship?.id && !isCreate) {
    navigate(Pages.CHAMPIONSHIPS.INDEX);
  }

  const isChampionshipSaving = useSelector(championshipISavingSelector);
  const isDelete = useSelector(championshipIsDeletingSelector);

  const hasChampionship = !!championship;
  const typeOptions = getTranslatedTeamPlayersNumberOptions(t);
  const teamPlayersNumber = championship?.settings?.teamPlayersNumber || TeamPlayersNumber.FIVE;
  const tournamentType = getTranslatedTeamPlayersNumberOption(t, teamPlayersNumber);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const { data, isFetching: isFeatureFetching } = useFetchAvailableFeature();
  const { OBS_INTEGRATION = false } = data ?? {};
  const isAdmin = useIsAdmin();

  const visibility = useMemo<VisibilityEnum[]>(() => {
    return isAdmin ? ['superAdmin', 'all'] : ['all'];
  }, [isAdmin]);

  const { data: templates, isFetching: isTemplatesFetching } = useQuery({
    queryKey: ['templates', tournamentId, visibility],
    queryFn: () => {
      return supabase.overlay.getOrCreateUserTemplate({ ownerId: tournamentId, visibility });
    },
    enabled: !!tournamentId,
    keepPreviousData: true,
    staleTime: 5 * time.minute,
  });

  const templatesOptions: Options<Template['id']> = useMemo(() => {
    if (isTemplatesFetching) return [];
    if (!templates?.length) return [];

    return templates.map((template) => ({
      label: template.name === 'ImGame (стандарт)' ? 'ImGame' : template.name,
      value: template.id,
    }));
  }, [isTemplatesFetching, templates]);

  const locationsOptions = useMemo(() => {
    return locationsToOptions(championship?.locations ?? []);
  }, [championship?.locations]);

  const initialValues = {
    name: championship?.name || '',
    description: championship?.description || '',
    startDate: championship?.startDate ? new Date(championship.startDate) : '',
    endDate: championship?.endDate ? new Date(championship.endDate) : '',
    tournamentType,
    locations: locationsOptions,
    template: _.find(templatesOptions, {
      value: championship?.settings?.overlayId,
    }),
  };

  const handleSubmit = useCallback(
    (values: FormikValues) => {
      const { locations, tournamentType, template, ...otherValues } = values;
      const locationIds = locations ? locations.map((loc: SelectOption<Location>) => loc.value) : [];
      if (state && state.highlight) {
        state.highlight = [];
      }

      const data = {
        ...otherValues,
        name: otherValues.name.trim(),
        description: otherValues.description.trim(),
        id: championship?.id,
        tournamentId,
        locationIds,
        teamPlayersNumber: tournamentType.value,
        overlayId: OBS_INTEGRATION
          ? (template?.value ?? templates?.[0]?.id ?? null)
          : (_.find(templates, { access: 'default' })?.id ?? null),
      };

      dispatch(upsertChampionship(data)).then((result) => {
        dispatch(fetchOrganizer({ tournamentId }));

        if (!hasChampionship) {
          StepperMainTool.setByPath('stepper:main:createTournament', true);
          const championshipId = unwrapResult(result);
          const pageUrl = Pages.CHAMPIONSHIPS.builders.edit(championshipId, 'rules');

          navigate(pageUrl, { replace: true });
        }
      });
    },
    [OBS_INTEGRATION, championship?.id, dispatch, hasChampionship, navigate, state, templates, tournamentId],
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), []);

  const deleteChampionship = useCallback(async () => {
    if (!championship?.id) {
      toast.warn('Турнир не найден');
      return;
    }

    try {
      await dispatch(deleteChampionshipAction({ championshipId: championship.id })).unwrap();
    } catch {
      toast.error('Не удалось удалить турнир');
      return;
    }

    toast.success('Турнир удален');
    closeDeleteModal();
    dispatch(removeChampionshipFromList({ id: championship.id }));
    navigate(Pages.CHAMPIONSHIPS.INDEX);
  }, [championship?.id, closeDeleteModal, dispatch, navigate]);

  const { handleClick: handleDeleteChampionship, isLoading: isDeleting } = useAsyncButton(deleteChampionship);

  const { PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.CHAMPIONSHIPS.INDEX,
  });

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={(values, formikHelpers) => {
          handleSubmit(values);
          formikHelpers.resetForm({ values }); // делаем текущие значения базой
          setIsFormDirty(false);
        }}
        validate={(values) => {
          const dirty = !_.isEqual(values, initialValues);
          if (dirty !== isFormDirty && !leavingConfirmed) {
            setIsFormDirty(dirty);
          }
          return {};
        }}
      >
        {(formProps) => {
          return (
            <Form onSubmit={formProps.handleSubmit}>
              <div className="mb-5 w-full gap-6 px-6">
                <div className="w-1/2">
                  <UDFormInput
                    autoFocus={isCreate}
                    name="name"
                    label={t('championship.info.nameInput.label')}
                    placeholder={t('championship.info.nameInput.placeholder')}
                    containerProps={{ className: 'mb-5' }}
                  />
                  <UDFormDateRange
                    autoComplete="off"
                    startName="startDate"
                    endName="endDate"
                    startLabel={t('championship.info.startDateInput.label')}
                    endLabel={t('championship.info.endDateInput.label')}
                    containerProps={{ className: 'mb-5' }}
                  />
                  <UDFormSelect
                    name="tournamentType"
                    label={t('championship.info.typeSelect.label')}
                    placeholder={t('championship.info.typeSelect.placeholder')}
                    containerProps={{ className: 'mb-5' }}
                    options={typeOptions}
                  />
                  <LocationSelect
                    name="locations"
                    label={t('championship.info.locationInput.label')}
                    placeholder={t('championship.info.locationInput.placeholder')}
                    containerProps={{ className: 'mb-5' }}
                    options={locationsOptions}
                    isMulti
                  />
                  <UdFormTemplateSelect
                    name="template"
                    label={t('championship.info.overlayInput.label')}
                    placeholder={t('championship.info.overlayInput.placeholder')}
                    containerProps={{ className: 'mb-5' }}
                    options={templatesOptions}
                    isAvailable={OBS_INTEGRATION}
                    highlight={state?.highlight?.includes('template')}
                    isLoading={isFeatureFetching}
                  />
                  <UDFormTextarea
                    name="description"
                    label={t('championship.info.descriptionInput.label')}
                    placeholder={t('championship.info.descriptionInput.placeholder')}
                    containerProps={{ className: 'mb-5' }}
                  />

                  <div className="flex gap-6">
                    <UDButton
                      variant="primary"
                      type="submit"
                      loading={isChampionshipSaving}
                      disabled={isChampionshipSaving || !formProps.dirty || isTemplatesFetching || isDelete || !formProps.isValid}
                      className="w-full"
                    >
                      {t('championship.info.saveButton.title')}
                    </UDButton>

                    {!isCreate && (
                      <UDButton
                        variant="secondary"
                        type="button"
                        disabled={isDelete || isTemplatesFetching || isChampionshipSaving}
                        className="w-full"
                        onClick={openDeleteModal}
                        style={{ color: 'red', outline: 'none' }}
                      >
                        {t('championship.info.deleteButton.title')}
                      </UDButton>
                    )}
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <Modal show={isDeleteModalOpen} onClose={closeDeleteModal}>
        <Modal.Header title="Подтвердить удаление" subtitle="Вы действительно хотите удалить турнир?" />
        <Modal.Footer justify="between">
          <UDButton variant="secondary" className="mb-3" type="button" onClick={closeDeleteModal} disabled={isDeleting} stretch>
            Отмена
          </UDButton>

          <UDButton variant="primary" className="mb-3" type="button" onClick={handleDeleteChampionship} loading={isDeleting} stretch>
            Удалить
          </UDButton>
        </Modal.Footer>
      </Modal>

      <PreventNavigationModal />
    </>
  );
};
